import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  CalendarPlus,
  CircleUser,
  HomeIcon,
  MessageSquareMore,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "../../hooks/useWindowSize";
import useIsLandscape from "../../hooks/useIsLandscape";

const TABLET = 1024;

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (_: boolean) => void;
}

export default function Sidebar({ isOpen, setIsOpen }: SidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const { t, i18n } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = useWindowSize(TABLET);
  const isLandscape = useIsLandscape();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMobile) return setIsOpen(false);
    setIsOpen(true);
  }, [isMobile, setIsOpen]);

  if (!isMounted || !i18n.isInitialized) {
    return null;
  }

  const menuItems = [
    { name: t("title.therapy"), path: "/", icon: <HomeIcon size={24} /> },
    {
      name: t("title.consultation"),
      path: "/consultation",
      icon: <CalendarPlus size={24} />,
    },
    {
      name: t("title.messages"),
      path: "/messages",
      icon: <MessageSquareMore size={24} />,
    },
    {
      name: t("title.profile"),
      path: "/profile",
      icon: <CircleUser size={24} />,
    },
  ];

  return (
    <>
      <div
        ref={dropdownRef}
        className={`fixed top-0 left-0 min-h-[100dvh] z-50 font-bold min-w-[120px] bg-primary-400 text-gray-900 p-6 flex flex-col justify-center transform transition-transform duration-300 
                    ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <nav className="flex flex-col space-y-2 z-40">
          {menuItems.map((item) => {
            const isActive =
              item.path === "/"
                ? pathname === "/" || pathname.startsWith("/exercises")
                : pathname.startsWith(item.path);

            return (
              <Link key={item.path} to={item.path}>
                <div
                  className={`text-[16px] flex flex-col items-center justify-center p-3 rounded-[60px] w-[90px] h-[120px] mx-auto mb-4 cursor-pointer transition-all 
                      ${isActive ? "bg-white text-primary-600" : "text-white hover:bg-white/20"}`}
                  onClick={() => {
                    if (isMobile) setIsOpen(false);
                  }}
                >
                  <div className="text-inherit">{item.icon}</div>
                  <span className="text-[16px] h-[24px] mt-1 font-bold text-inherit text-center leading-tight">
                    {item.name}
                  </span>
                </div>
              </Link>
            );
          })}
        </nav>
      </div>

      {!(isMobile && isLandscape) && (
        <div
          className={`fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 flex justify-around shadow-md
                    transform transition-transform duration-300
                    ${isOpen ? "translate-y-full" : "translate-y-0"}
                `}
        >
          {menuItems.map((item) => {
            const isActive =
              item.path === "/"
                ? pathname === "/" || pathname.startsWith("/exercises")
                : pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center"
              >
                <div
                  className={`w-8 h-1 rounded mb-1 transition-all duration-300 ${
                    isActive ? "bg-primary-500" : "bg-transparent"
                  }`}
                />
                <div
                  className={`${
                    isActive ? "text-orange-500 font-bold" : "text-gray-500"
                  } py-2 flex flex-col items-center justify-center`}
                >
                  {item.icon}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
